import { useState, useEffect, useCallback } from 'react';
import { LocalStorageManager } from '../utils/localStorage';

interface UsePersistedStateConfig<T> {
    key: string;
    defaultValue: T;
    validator?: (value: any) => value is T;
}

/**
 * Hook that automatically persists state changes to localStorage
 * @param initialState - Initial state value
 * @param config - Configuration object with key, defaultValue, and optional validator
 * @returns [state, setState] tuple similar to useState
 */
export function usePersistedState<T>(
    initialState: T,
    config: UsePersistedStateConfig<T>,
): [T, React.Dispatch<React.SetStateAction<T>>] {
    // Initialize state from localStorage or use provided initial state
    const [state, setState] = useState<T>(() => {
        if (!LocalStorageManager.isAvailable()) {
            return initialState;
        }

        const savedState = LocalStorageManager.get({
            key: config.key,
            defaultValue: config.defaultValue,
            validator: config.validator,
        });

        // If saved state is equal to default, use initial state; otherwise use saved state
        return JSON.stringify(savedState) ===
            JSON.stringify(config.defaultValue)
            ? initialState
            : savedState;
    });

    // Auto-persist state changes to localStorage
    useEffect(() => {
        if (LocalStorageManager.isAvailable()) {
            LocalStorageManager.set(config.key, state);
        }
    }, [state, config.key]);

    return [state, setState];
}

/**
 * Hook that provides localStorage operations without automatic persistence
 * Useful when you need more control over when persistence occurs
 */
export function useLocalStorageOperations<T>(
    config: UsePersistedStateConfig<T>,
) {
    const getSavedState = useCallback((): T => {
        if (!LocalStorageManager.isAvailable()) {
            return config.defaultValue;
        }

        return LocalStorageManager.get({
            key: config.key,
            defaultValue: config.defaultValue,
            validator: config.validator,
        });
    }, [config.key, config.defaultValue, config.validator]);

    const saveState = useCallback(
        (state: T) => {
            if (LocalStorageManager.isAvailable()) {
                LocalStorageManager.set(config.key, state);
            }
        },
        [config.key],
    );

    const clearState = useCallback(() => {
        if (LocalStorageManager.isAvailable()) {
            LocalStorageManager.remove(config.key);
        }
    }, [config.key]);

    return {
        getSavedState,
        saveState,
        clearState,
    };
}
